import { toNotificationDTO } from "./notification-policy";
/**
 * Cliente REST del centro de notificaciones — `GET/PATCH /api/notifications/*`
 * (ADR 0022, fase 3 del worker ya mergeada — ver `notifications-router.ts`).
 *
 * Mismo patrón que `lib/platform.ts`: `credentials: "include"` + header
 * `X-Requested-With` en toda mutación (requerido por `requireCsrfDefense`
 * del worker). Wrapper propio (no se reusa `platformFetch`) porque el path
 * base es `/api/notifications`, no `/api/platform` — mismo motivo por el que
 * `platform-org-api.ts` sí reusa `platformFetch` (comparte prefijo) y este
 * módulo no.
 */
import type { NotificationApiRow, NotificationDTO } from "./types";

// ---------------------------------------------------------------------------
// Fetch base
// ---------------------------------------------------------------------------

export function getApiBaseUrl(): string {
  return import.meta.env.VITE_API_URL || "http://localhost:6787";
}

const MUTATION_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

async function notificationFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);

  if (MUTATION_METHODS.has(method)) {
    headers.set("X-Requested-With", "portal-dashboard");
    if (!headers.has("Content-Type") && init.body) {
      headers.set("Content-Type", "application/json");
    }
  }

  return fetch(`${getApiBaseUrl()}${path}`, {
    ...init,
    credentials: "include",
    headers,
  });
}

// ---------------------------------------------------------------------------
// Error tipado — mismo patrón que ApiError de platform-api.ts
// ---------------------------------------------------------------------------

export class NotificationApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "NotificationApiError";
  }
}

async function toApiError(res: Response, fallback: string): Promise<NotificationApiError> {
  const payload = (await res.json().catch(() => ({}))) as { error?: string };
  return new NotificationApiError(res.status, payload.error ?? fallback);
}

// ---------------------------------------------------------------------------
// GET / — lista visibles (?since= para el catch-up de reconexión, ADR §4)
// ---------------------------------------------------------------------------

export interface FetchNotificationsOptions {
  since?: string | undefined;
  limit?: number | undefined;
  /**
   * Espeja el enum `HiddenFilter` del worker (`notifications-router.ts`).
   * `"only"` trae la vista "Ocultas"; sin este campo el servidor usa su
   * default `"excluded"` (bandeja normal) — por eso no hace falta mandar
   * `"excluded"` explícito en la carga inicial, solo cuando el toggle pide
   * la otra vista.
   */
  hidden?: "excluded" | "only" | undefined;
}

export async function fetchNotifications(
  opts: FetchNotificationsOptions = {},
): Promise<NotificationDTO[]> {
  const params = new URLSearchParams();
  if (opts.since) params.set("since", opts.since);
  if (opts.limit !== undefined) params.set("limit", String(opts.limit));
  if (opts.hidden) params.set("hidden", opts.hidden);
  const qs = params.toString();

  const res = await notificationFetch(`/api/notifications${qs ? `?${qs}` : ""}`);
  if (!res.ok) {
    throw await toApiError(res, "Error al cargar notificaciones");
  }
  const data = (await res.json()) as { notifications: NotificationApiRow[] };
  return data.notifications.map(toNotificationDTO);
}

// ---------------------------------------------------------------------------
// PATCH /:id/read | /:id/hide | /:id/unhide
// ---------------------------------------------------------------------------

async function patchNotification(
  id: string,
  action: "read" | "hide" | "unhide",
): Promise<NotificationDTO> {
  const res = await notificationFetch(`/api/notifications/${encodeURIComponent(id)}/${action}`, {
    method: "PATCH",
  });
  if (!res.ok) {
    const fallback =
      action === "read"
        ? "Error al marcar como vista"
        : action === "hide"
          ? "Error al ocultar la notificación"
          : "Error al restaurar la notificación";
    throw await toApiError(res, fallback);
  }
  const data = (await res.json()) as { notification: NotificationApiRow };
  return toNotificationDTO(data.notification);
}

/** `PATCH /:id/read` — marca como vista (`readAt = now()`). */
export function markNotificationRead(id: string): Promise<NotificationDTO> {
  return patchNotification(id, "read");
}

/** `PATCH /:id/hide` — soft-delete reversible (`hiddenAt = now()`). */
export function hideNotification(id: string): Promise<NotificationDTO> {
  return patchNotification(id, "hide");
}

/** `PATCH /:id/unhide` — deshace el ocultamiento (`hiddenAt = NULL`). */
export function unhideNotification(id: string): Promise<NotificationDTO> {
  return patchNotification(id, "unhide");
}
